import React from 'react';
import { styled, Box, FormControl, InputV1, InputV2, TextV2, css, Flex, SpacingStack, ButtonV1, FormField, StyleSystemProps } from '@withjoy/joykit';
import { animated } from 'react-spring';
import { ReactComponent as HelpIcon } from '@assets/icons/help.svg';
import { useAddCashFundDialogController } from './AddCashFundDialog.controller';
import { Tabs } from '@shared/components/Tabs/Tabs';

export const StyledSpacingStackPhoto = styled(SpacingStack)`
  height: 100%;
  justify-content: center;
`;

export const StyledPhoto = styled(Box)<{ $backgroundImage?: string | null; thumbnail?: boolean | null; isSuccessStep?: boolean | null }>`
  background-image: ${({ $backgroundImage }) => ($backgroundImage ? `url(${$backgroundImage})` : undefined)};
  background-size: 100%;
  background-repeat: no-repeat;
  background-position: center;
  align-self: center;
  position: relative;
  display: flex;
  flex-direction: column;
  border: 1px solid ${props => props.theme.colors.mono5};
  border-radius: 8px;
  transition: all ease-in-out 0.1s;
  ${({ thumbnail }) => (thumbnail ? `${EditPhotoButton} {opacity: 0;}` : ``)};
  ${props => {
    if (!props.isSuccessStep) {
      return css`
        cursor: pointer;
        &:hover {
          border: 1px solid ${props => props.theme.colors.strokeHover};
          ${props.thumbnail
            ? `${EditPhotoButton} {opacity: 1;}`
            : `
          box-shadow: inset 0px 0px 32px rgba(0, 0, 0, 0.8);
          background-size: 110%;
          transition: all ease-in-out 0.2s;
        `}
        }
        &:active,
        &:target {
          border: 1px solid ${props => props.theme.colors.strokeActive};
          box-shadow: inset 0px 0px 8px rgba(0, 0, 0, 0.8);
          background-size: 100%;
        }
      `;
    }
    return;
  }}
  will-change: width, height, border-radius;
`;

export const EditPhotoButton = styled.a<{ disabled?: boolean | false; thumbnail?: boolean | null }>`
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  width: 48px;
  height: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  backdrop-filter: blur(32px);
  background-color: ${props => props.theme.colors.gray2};
  border-radius: 100%;

  ${({ thumbnail }) =>
    thumbnail
      ? `
      margin: 0 auto;
    `
      : `position: absolute;
      bottom: 16px;
      right: 16px;`}
`;

export const AnimatedStyledPhoto = animated(StyledPhoto);

export const StyledDialogHeader = styled(Box)`
  display: block;
  text-align: center;
  will-change: background-color, height;
`;

export const AnimatedDialogHeader = animated(StyledDialogHeader);

export const StyledDialogBody = styled(Flex)<{ isMobile?: boolean; isCharityPaymentStep?: boolean }>`
  position: relative;
  border-radius: 16px;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
  height: 100%;
  padding: ${({ isMobile, isCharityPaymentStep }) => (isMobile ? '64px 40px 120px' : isCharityPaymentStep ? '24px 52px 52px' : '32px 52px 52px')};
`;

const StyledFlexWrapper = styled(Flex)`
  flex-direction: column;
  flex-grow: 1;
  will-change: opacity, height, transform;
`;

export const AnimatedBox = animated(StyledFlexWrapper);

export const StyledNumericFormControl = styled(FormControl)<{ isEditDialog?: boolean; isCustomPadding?: boolean }>`
  > label {
    text-align: ${({ isEditDialog }) => (isEditDialog ? 'flex-start' : 'center')};
  }
  input {
    ${({ isCustomPadding }) => (isCustomPadding ? 'padding-left: 32px !important;' : '')};
    text-align: ${({ isEditDialog }) => (isEditDialog ? 'left' : 'center')};
    &[type='number'] {
      -moz-appearance: textfield;
    }
  }
  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

export const StyledNumericInput = styled(InputV2)<{ isEditDialog?: boolean; isCustomPadding?: boolean }>`
  text-align: ${({ isEditDialog }) => (isEditDialog ? 'left' : 'center')};
  ${({ isCustomPadding }) => (isCustomPadding ? 'padding-left: 32px !important;' : '')};
`;

export const StyledInputPrefix = styled.span<{ prefixOffset: number; isFixedPrefix?: boolean }>`
  line-height: 22px;
  font-size: 15px;
  position: absolute;
  top: 50%;
  left: ${({ isFixedPrefix, prefixOffset }) => (isFixedPrefix ? `${prefixOffset}px` : '50%')};
  ${({ isFixedPrefix, prefixOffset }) =>
    !isFixedPrefix && prefixOffset ? `transform: translate(calc(-50% - ${prefixOffset / 2}px - 4px), -50%)` : `transform: translate(0 , -50%)`};
`;

export const HeaderNameInput = styled(InputV1)`
  display: inline-block;
  text-align: center;
  font-weight: bold;
  font-size: 24px;
  background: inherit;
  border-radius: 8px;
  min-height: 40px;
  cursor: pointer;
  border-color: transparent;
  &:hover {
    border: 2px solid ${props => props.theme.colors.mono5};
    color: ${props => props.theme.colors.fillDarkHover};
  }
  &:focus {
    border: 2px solid ${props => props.theme.colors.strokeActive};
  }
`;

export const StyledSummary = styled(Box)<{ useHoverState?: boolean }>`
  display: inline-block;
  font-size: 17px;
  font-style: normal;
  font-weight: 600;
  letter-spacing: -0.01em;
  border: 1px solid transparent;
  padding: 6px 12px;
  border-radius: 8px;
  position: relative;
  cursor: pointer;
  span {
    color: ${props => props.theme.colors.mono10};
    margin-left: 8px;
  }
  svg:last-of-type {
    position: absolute;
    right: -20px;
    opacity: 0;
  }
  &:hover {
    border: 1px solid ${props => props.theme.colors.mono5};
    color: ${props => props.theme.colors.fillDarkHover};
    background: ${props => props.theme.colors.white};
    span {
      color: inherit;
    }
    svg:last-of-type {
      opacity: 1;
    }
  }

  ${props =>
    props.useHoverState
      ? css`
          border: 1px solid ${props => props.theme.colors.mono5};
          color: ${props => props.theme.colors.fillDarkHover};
          background: ${props => props.theme.colors.white};
          span {
            color: inherit;
          }
          span:last-of-type {
            opacity: 1;
          }
        `
      : ``};
`;

export const InlineFormControl = styled(FormControl)<{ isSelect?: boolean }>`
  ${props =>
    props.isMobile
      ? css`
          width: 100%;
          z-index: ${props.isSelect ? 2 : 'inherit'};
        `
      : css`
          display: inline-block;
          width: ${props.width ? props.width : ''};
          ul {
            min-width: initial !important;
          }
        `}
`;

export const CenteredFormField = styled(FormField)`
  text-align: center;
`;

export const StyledHeaderText = styled(TextV2)`
  line-height: 32px;
  border: none;
  width: 100%;
`;

export const AnimatedText = animated(Box);

export const StyledCancelWrapper = styled(Flex)`
  flex-grow: 1;
`;

export const StyledAbortCancelButton = styled(ButtonV1)`
  color: ${props => props.theme.colors.mono14};
  background-color: transparent;
  :hover,
  :active,
  :focus {
    background-color: transparent !important;
  }
`;

export const StyledClose = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  padding: 8px;
  z-index: 1000;
`;

export const StyledHelpIcon = styled(HelpIcon)`
  cursor: pointer;
  &:hover {
    path {
      fill: ${props => props.theme.colors.linkHover};
    }
  }
  &:active {
    path {
      fill: ${props => props.theme.colors.linkActive};
    }
  }
  margin-left: 0.375rem;
`;

type PaymentMethodChipsProps = {
  selectedPaymentMethod: ReturnType<typeof useAddCashFundDialogController>['selectedPaymentMethod'];
  cashOrCheckText: string;
  enableCashOrCheck: boolean;
  enableJoyCredit: boolean;
  enableJoyCreditText: string;
  enableAppPlatform: boolean;
};

export const PaymentMethodsChips = ({
  selectedPaymentMethod,
  enableCashOrCheck,
  cashOrCheckText,
  enableJoyCredit,
  enableJoyCreditText,
  enableAppPlatform
}: PaymentMethodChipsProps) => {
  return (
    <Flex flexDirection="row" marginTop="10px" marginBottom="4px">
      {enableAppPlatform && (
        <Chip $left $right={!enableJoyCredit}>
          <ChipText>{selectedPaymentMethod?.label}</ChipText>
        </Chip>
      )}
      {enableJoyCredit && (
        <Chip $left={!enableAppPlatform} $right={!cashOrCheckText}>
          <ChipText>{enableJoyCreditText}</ChipText>
        </Chip>
      )}
      {enableCashOrCheck && (
        <Chip $right>
          <ChipText>{cashOrCheckText}</ChipText>
        </Chip>
      )}
    </Flex>
  );
};

type ChipProps = {
  $left?: boolean;
  $right?: boolean;
};

const Chip = styled(Box)<ChipProps>`
  border: 1px solid ${props => props.theme.colors.mono5};
  border-top-left-radius: ${({ $left }) => `${$left ? '4' : '0'}px`};
  border-bottom-left-radius: ${({ $left }) => `${$left ? '4' : '0'}px`};
  border-top-right-radius: ${({ $right }) => `${$right ? '4' : '0'}px`};
  border-bottom-right-radius: ${({ $right }) => `${$right ? '4' : '0'}px`};
  border-right: ${({ theme, $left, $right }) => `${$left && !$right ? '0px' : '1px'} solid ${theme.colors.mono5}`};
  padding-top: 4px;
  padding-bottom: 4px;
  padding-left: 8px;
  padding-right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ChipText = styled(TextV2)`
  color: ${props => props.theme.colors.typePrimaryDark};
  font-size: 13px;
  font-weight: 400;
  letter-spacing: -2%;
  line-height: 18px;
`;

export const StyledTabs = styled(Tabs)`
  > span {
    width: 48%;
  }

  > div > span {
    width: 48%;
  }

  > div > div > span {
    width: 48%;
  }
`;

const iconButton: StyleSystemProps = {
  backgroundColor: 'white',
  position: 'absolute',
  top: 5,
  size: 8,
  borderRadius: '100%',
  zIndex: 3
};

export const styles = {
  iconButton
};
